import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { TEMPLATE_LIBRARY, generateBlockId } from './templateLibrary';
import DisclaimerBlockPicker from '../../components/DisclaimerBlockPicker';

const SHAPE_OPTIONS = [
  { id: 'rectangle', label: 'Rectangle', icon: '▬' },
  { id: 'circle', label: 'Circle', icon: '●' },
  { id: 'line_h', label: 'Horizontal Line', icon: '―' },
  { id: 'line_v', label: 'Vertical Line', icon: '│' },
  { id: 'arrow_right', label: 'Arrow Right', icon: '→' },
  { id: 'arrow_down', label: 'Arrow Down', icon: '↓' },
  { id: 'rounded_rect', label: 'Rounded Rectangle', icon: '▢' },
  { id: 'triangle', label: 'Triangle', icon: '△' },
];

// ── SVG thumbnail previews for each template ────────────────────
const PREVIEW_THUMBNAILS = {
  // Charts
  perf_line_chart: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <polyline points="20,90 50,70 80,75 110,50 140,55 170,30" stroke="#072040" strokeWidth="2" fill="none" />
      <polyline points="20,92 50,80 80,82 110,65 140,68 170,50" stroke="#f0a500" strokeWidth="2" fill="none" strokeDasharray="4,3" />
      <line x1="20" y1="100" x2="180" y2="100" stroke="#dee2e6" strokeWidth="1" />
      <line x1="20" y1="20" x2="20" y2="100" stroke="#dee2e6" strokeWidth="1" />
      <text x="100" y="14" textAnchor="middle" fontSize="8" fill="#6c757d">Portfolio vs Benchmark</text>
    </svg>
  ),
  allocation_donut: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <circle cx="100" cy="60" r="40" fill="none" stroke="#072040" strokeWidth="16" strokeDasharray="80 171" strokeDashoffset="0" />
      <circle cx="100" cy="60" r="40" fill="none" stroke="#599BE3" strokeWidth="16" strokeDasharray="50 201" strokeDashoffset="-80" />
      <circle cx="100" cy="60" r="40" fill="none" stroke="#f0a500" strokeWidth="16" strokeDasharray="40 211" strokeDashoffset="-130" />
      <circle cx="100" cy="60" r="40" fill="none" stroke="#91CF9B" strokeWidth="16" strokeDasharray="30 221" strokeDashoffset="-170" />
      <circle cx="100" cy="60" r="24" fill="#f8f9fa" />
      <text x="100" y="14" textAnchor="middle" fontSize="8" fill="#6c757d">Asset Allocation</text>
    </svg>
  ),
  allocation_vs_saa_bar: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <rect x="25" y="40" width="12" height="55" fill="#072040" /><rect x="39" y="50" width="12" height="45" fill="#599BE3" />
      <rect x="60" y="30" width="12" height="65" fill="#072040" /><rect x="74" y="35" width="12" height="60" fill="#599BE3" />
      <rect x="95" y="55" width="12" height="40" fill="#072040" /><rect x="109" y="50" width="12" height="45" fill="#599BE3" />
      <rect x="130" y="60" width="12" height="35" fill="#072040" /><rect x="144" y="65" width="12" height="30" fill="#599BE3" />
      <line x1="20" y1="95" x2="180" y2="95" stroke="#dee2e6" strokeWidth="1" />
      <rect x="60" y="8" width="8" height="5" fill="#072040" /><text x="71" y="13" fontSize="6" fill="#6c757d">Actual</text>
      <rect x="100" y="8" width="8" height="5" fill="#599BE3" /><text x="111" y="13" fontSize="6" fill="#6c757d">SAA</text>
    </svg>
  ),
  active_tilts_bar: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <line x1="100" y1="15" x2="100" y2="105" stroke="#dee2e6" strokeWidth="1" />
      <rect x="100" y="20" width="50" height="12" fill="#599BE3" />
      <rect x="100" y="38" width="30" height="12" fill="#599BE3" />
      <rect x="60" y="56" width="40" height="12" fill="#f0a500" />
      <rect x="100" y="74" width="20" height="12" fill="#599BE3" />
      <rect x="75" y="92" width="25" height="12" fill="#f0a500" />
      <text x="100" y="12" textAnchor="middle" fontSize="8" fill="#6c757d">Active Tilts</text>
    </svg>
  ),
  historical_allocation_area: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <polygon points="20,100 20,60 60,58 100,55 140,52 180,50 180,100" fill="#072040" opacity="0.8" />
      <polygon points="20,60 20,40 60,38 100,36 140,34 180,32 180,50 140,52 100,55 60,58" fill="#599BE3" opacity="0.7" />
      <polygon points="20,40 20,25 60,24 100,22 140,21 180,20 180,32 140,34 100,36 60,38" fill="#f0a500" opacity="0.6" />
      <line x1="20" y1="100" x2="180" y2="100" stroke="#dee2e6" strokeWidth="1" />
      <text x="100" y="14" textAnchor="middle" fontSize="8" fill="#6c757d">Historical Allocation</text>
    </svg>
  ),
  credit_exposure_bar: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <rect x="30" y="30" width="24" height="65" fill="#072040" />
      <rect x="62" y="45" width="24" height="50" fill="#072040" />
      <rect x="94" y="55" width="24" height="40" fill="#072040" />
      <rect x="126" y="65" width="24" height="30" fill="#072040" />
      <rect x="158" y="80" width="24" height="15" fill="#072040" />
      <line x1="25" y1="95" x2="185" y2="95" stroke="#dee2e6" strokeWidth="1" />
      <text x="42" y="105" textAnchor="middle" fontSize="6" fill="#6c757d">AAA</text>
      <text x="74" y="105" textAnchor="middle" fontSize="6" fill="#6c757d">AA</text>
      <text x="106" y="105" textAnchor="middle" fontSize="6" fill="#6c757d">A</text>
      <text x="138" y="105" textAnchor="middle" fontSize="6" fill="#6c757d">BBB</text>
      <text x="170" y="105" textAnchor="middle" fontSize="6" fill="#6c757d">BB-</text>
    </svg>
  ),
  fs_allocation_bar: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <rect x="30" y="35" width="24" height="60" fill="#072040" />
      <rect x="62" y="25" width="24" height="70" fill="#599BE3" />
      <rect x="94" y="50" width="24" height="45" fill="#f0a500" />
      <rect x="126" y="60" width="24" height="35" fill="#91CF9B" />
      <rect x="158" y="70" width="24" height="25" fill="#6c757d" />
      <line x1="25" y1="95" x2="185" y2="95" stroke="#dee2e6" strokeWidth="1" />
      <text x="100" y="14" textAnchor="middle" fontSize="8" fill="#6c757d">Allocation Bar</text>
    </svg>
  ),
  fs_credit_pie: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <circle cx="80" cy="60" r="40" fill="#072040" />
      <path d="M80,60 L80,20 A40,40 0 0,1 114,40 Z" fill="#599BE3" />
      <path d="M80,60 L114,40 A40,40 0 0,1 120,60 Z" fill="#f0a500" />
      <rect x="135" y="35" width="8" height="6" fill="#072040" /><text x="146" y="41" fontSize="6" fill="#6c757d">AAA</text>
      <rect x="135" y="48" width="8" height="6" fill="#599BE3" /><text x="146" y="54" fontSize="6" fill="#6c757d">AA</text>
      <rect x="135" y="61" width="8" height="6" fill="#f0a500" /><text x="146" y="67" fontSize="6" fill="#6c757d">A</text>
    </svg>
  ),
  fs_performance_line: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <polyline points="20,85 50,70 80,65 110,45 140,40 170,25" stroke="#072040" strokeWidth="2" fill="none" />
      <polyline points="20,88 50,78 80,76 110,62 140,60 170,48" stroke="#599BE3" strokeWidth="1.5" fill="none" strokeDasharray="3,2" />
      <polyline points="20,90 50,82 80,78 110,70 140,66 170,58" stroke="#6c757d" strokeWidth="1" fill="none" strokeDasharray="2,2" />
      <line x1="20" y1="100" x2="180" y2="100" stroke="#dee2e6" strokeWidth="1" />
      <text x="100" y="14" textAnchor="middle" fontSize="8" fill="#6c757d">Performance Since Inception</text>
    </svg>
  ),
  waterfall_chart: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <line x1="20" y1="70" x2="185" y2="70" stroke="#dee2e6" strokeWidth="0.5" strokeDasharray="2,2" />
      <rect x="25" y="40" width="18" height="30" fill="#91CF9B" />
      <rect x="48" y="45" width="18" height="25" fill="#91CF9B" />
      <rect x="71" y="50" width="18" height="20" fill="#91CF9B" />
      <rect x="94" y="55" width="18" height="15" fill="#91CF9B" />
      <rect x="117" y="70" width="18" height="15" fill="#f0a500" />
      <rect x="140" y="70" width="18" height="22" fill="#f0a500" />
      <rect x="163" y="70" width="18" height="28" fill="#f0a500" />
      <line x1="20" y1="100" x2="185" y2="100" stroke="#dee2e6" strokeWidth="1" />
      <text x="100" y="14" textAnchor="middle" fontSize="8" fill="#6c757d">Contribution Waterfall</text>
    </svg>
  ),
  sector_exposure_chart: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <rect x="20" y="35" width="14" height="60" fill="#072040" /><rect x="36" y="40" width="14" height="55" fill="#599BE3" />
      <rect x="58" y="25" width="14" height="70" fill="#072040" /><rect x="74" y="30" width="14" height="65" fill="#599BE3" />
      <rect x="96" y="50" width="14" height="45" fill="#072040" /><rect x="112" y="45" width="14" height="50" fill="#599BE3" />
      <rect x="134" y="55" width="14" height="40" fill="#072040" /><rect x="150" y="60" width="14" height="35" fill="#599BE3" />
      <line x1="15" y1="95" x2="185" y2="95" stroke="#dee2e6" strokeWidth="1" />
      <rect x="55" y="8" width="8" height="5" fill="#072040" /><text x="66" y="13" fontSize="6" fill="#6c757d">Actual</text>
      <rect x="100" y="8" width="8" height="5" fill="#599BE3" /><text x="111" y="13" fontSize="6" fill="#6c757d">SAA</text>
    </svg>
  ),
  risk_return_scatter: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <line x1="30" y1="100" x2="180" y2="100" stroke="#dee2e6" strokeWidth="1" />
      <line x1="30" y1="15" x2="30" y2="100" stroke="#dee2e6" strokeWidth="1" />
      <circle cx="90" cy="45" r="7" fill="#072040" />
      <circle cx="120" cy="55" r="7" fill="#f0a500" />
      <text x="105" y="112" textAnchor="middle" fontSize="7" fill="#6c757d">Volatility</text>
      <text x="15" y="60" textAnchor="middle" fontSize="7" fill="#6c757d" transform="rotate(-90,15,60)">Return</text>
      <rect x="60" y="8" width="6" height="5" fill="#072040" rx="3" /><text x="70" y="13" fontSize="6" fill="#6c757d">Portfolio</text>
      <rect x="110" y="8" width="6" height="5" fill="#f0a500" rx="3" /><text x="120" y="13" fontSize="6" fill="#6c757d">Peer</text>
    </svg>
  ),
  drawdown_chart: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <line x1="20" y1="25" x2="180" y2="25" stroke="#dee2e6" strokeWidth="1" />
      <text x="15" y="28" textAnchor="end" fontSize="6" fill="#6c757d">0%</text>
      <path d="M20,25 L40,25 L55,40 L65,55 L75,45 L90,25 L105,30 L115,60 L130,80 L145,65 L155,40 L170,30 L180,25" stroke="#f0a500" strokeWidth="2" fill="none" />
      <path d="M20,25 L40,25 L55,40 L65,55 L75,45 L90,25 L105,30 L115,60 L130,80 L145,65 L155,40 L170,30 L180,25 L180,25 L20,25 Z" fill="rgba(240,165,0,0.15)" />
      <line x1="20" y1="100" x2="180" y2="100" stroke="#dee2e6" strokeWidth="1" />
      <text x="100" y="14" textAnchor="middle" fontSize="8" fill="#6c757d">Drawdown from Peak</text>
    </svg>
  ),
  growth_defensive_chart: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <rect x="20" y="40" width="105" height="35" rx="3" fill="#072040" />
      <rect x="125" y="40" width="60" height="35" rx="3" fill="#599BE3" />
      <text x="72" y="62" textAnchor="middle" fontSize="10" fill="white" fontWeight="bold">65%</text>
      <text x="155" y="62" textAnchor="middle" fontSize="10" fill="white" fontWeight="bold">35%</text>
      <rect x="55" y="90" width="8" height="6" fill="#072040" /><text x="67" y="96" fontSize="7" fill="#6c757d">Growth</text>
      <rect x="105" y="90" width="8" height="6" fill="#599BE3" /><text x="117" y="96" fontSize="7" fill="#6c757d">Defensive</text>
    </svg>
  ),
  // Tables
  perf_risk_table: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <rect x="10" y="15" width="180" height="16" rx="2" fill="#072040" />
      <text x="30" y="26" fontSize="7" fill="white">Metric</text>
      <text x="100" y="26" fontSize="7" fill="white">3M</text>
      <text x="130" y="26" fontSize="7" fill="white">1Y</text>
      <text x="160" y="26" fontSize="7" fill="white">3Y</text>
      {[0,1,2,3,4].map(i => <rect key={i} x="10" y={35 + i*16} width="180" height="14" fill={i%2 ? '#f8f9fa' : '#fff'} stroke="#eee" strokeWidth="0.5" />)}
      <text x="30" y="45" fontSize="6" fill="#333">Return</text><text x="100" y="45" fontSize="6" fill="#333">2.1%</text>
      <text x="30" y="61" fontSize="6" fill="#333">Peer</text><text x="100" y="61" fontSize="6" fill="#333">1.8%</text>
      <text x="30" y="77" fontSize="6" fill="#333">Sharpe</text><text x="100" y="77" fontSize="6" fill="#333">0.85</text>
      <text x="30" y="93" fontSize="6" fill="#333">Vol</text><text x="100" y="93" fontSize="6" fill="#333">8.2%</text>
    </svg>
  ),
  perf_risk_monthly_table: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <rect x="10" y="15" width="180" height="16" rx="2" fill="#072040" />
      <text x="30" y="26" fontSize="7" fill="white">Metric</text>
      <text x="80" y="26" fontSize="7" fill="white">1M</text>
      <text x="105" y="26" fontSize="7" fill="white">3M</text>
      <text x="130" y="26" fontSize="7" fill="white">1Y</text>
      <text x="160" y="26" fontSize="7" fill="white">3Y</text>
      {[0,1,2,3,4].map(i => <rect key={i} x="10" y={35 + i*16} width="180" height="14" fill={i%2 ? '#f8f9fa' : '#fff'} stroke="#eee" strokeWidth="0.5" />)}
      <text x="30" y="45" fontSize="6" fill="#333">Return</text><text x="80" y="45" fontSize="6" fill="#333">0.8%</text><text x="105" y="45" fontSize="6" fill="#333">2.1%</text>
      <text x="30" y="61" fontSize="6" fill="#333">Peer</text><text x="80" y="61" fontSize="6" fill="#333">0.5%</text><text x="105" y="61" fontSize="6" fill="#333">1.8%</text>
      <text x="30" y="77" fontSize="6" fill="#333">Sharpe</text><text x="105" y="77" fontSize="6" fill="#333">0.85</text>
      <text x="30" y="93" fontSize="6" fill="#333">Vol</text><text x="105" y="93" fontSize="6" fill="#333">8.2%</text>
    </svg>
  ),
  fy_returns_table: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <rect x="10" y="15" width="180" height="16" rx="2" fill="#072040" />
      <text x="30" y="26" fontSize="7" fill="white">FY</text>
      <text x="90" y="26" fontSize="7" fill="white">Portfolio</text>
      <text x="150" y="26" fontSize="7" fill="white">Bench</text>
      {[0,1,2,3].map(i => <rect key={i} x="10" y={35 + i*18} width="180" height="16" fill={i%2 ? '#f8f9fa' : '#fff'} stroke="#eee" strokeWidth="0.5" />)}
      <text x="30" y="47" fontSize="6" fill="#333">FY24</text><text x="100" y="47" fontSize="6" fill="#072040">8.5%</text>
      <text x="30" y="65" fontSize="6" fill="#333">FY23</text><text x="100" y="65" fontSize="6" fill="#072040">6.2%</text>
      <text x="30" y="83" fontSize="6" fill="#333">FY22</text><text x="100" y="83" fontSize="6" fill="#f0a500">-3.1%</text>
    </svg>
  ),
  holdings_table: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <rect x="10" y="15" width="180" height="16" rx="2" fill="#072040" />
      <text x="20" y="26" fontSize="7" fill="white">Holding</text>
      <text x="140" y="26" fontSize="7" fill="white">Weight</text>
      {[0,1,2,3,4].map(i => <rect key={i} x="10" y={35 + i*15} width="180" height="13" fill={i%2 ? '#f8f9fa' : '#fff'} stroke="#eee" strokeWidth="0.5" />)}
      <text x="20" y="45" fontSize="6" fill="#333">Fund A</text><text x="145" y="45" fontSize="6" fill="#333">12.5%</text>
      <text x="20" y="60" fontSize="6" fill="#333">Fund B</text><text x="145" y="60" fontSize="6" fill="#333">10.2%</text>
      <text x="20" y="75" fontSize="6" fill="#333">Fund C</text><text x="145" y="75" fontSize="6" fill="#333">8.8%</text>
    </svg>
  ),
  fs_holdings_by_class: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <rect x="10" y="10" width="180" height="14" rx="2" fill="#072040" />
      <text x="20" y="20" fontSize="7" fill="white">Asset Class</text><text x="110" y="20" fontSize="7" fill="white">Fund</text><text x="165" y="20" fontSize="7" fill="white">Wt%</text>
      <rect x="10" y="27" width="90" height="12" fill="#E7E6E6" /><text x="15" y="36" fontSize="6" fill="#333" fontWeight="bold">Aus Equities</text>
      {[0,1].map(i => <React.Fragment key={i}><rect x="10" y={41+i*12} width="180" height="11" fill="#fff" stroke="#eee" strokeWidth="0.5" /><text x="110" y={49+i*12} fontSize="6" fill="#333">Fund {i+1}</text></React.Fragment>)}
      <rect x="10" y="66" width="90" height="12" fill="#E7E6E6" /><text x="15" y="75" fontSize="6" fill="#333" fontWeight="bold">Int Equities</text>
      {[0,1].map(i => <React.Fragment key={i}><rect x="10" y={80+i*12} width="180" height="11" fill="#fff" stroke="#eee" strokeWidth="0.5" /><text x="110" y={88+i*12} fontSize="6" fill="#333">Fund {i+3}</text></React.Fragment>)}
    </svg>
  ),
  fs_top10_holdings: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <rect x="10" y="10" width="180" height="14" rx="2" fill="#072040" />
      <text x="20" y="20" fontSize="7" fill="white">Stock</text><text x="110" y="20" fontSize="7" fill="white">Country</text><text x="165" y="20" fontSize="7" fill="white">Wt%</text>
      {[0,1,2,3,4,5].map(i => <rect key={i} x="10" y={27 + i*14} width="180" height="12" fill={i%2 ? '#f8f9fa' : '#fff'} stroke="#eee" strokeWidth="0.5" />)}
      <text x="20" y="37" fontSize="6" fill="#333">BHP Group</text><text x="115" y="37" fontSize="6" fill="#333">AU</text><text x="170" y="37" fontSize="6" fill="#333">3.2%</text>
      <text x="20" y="51" fontSize="6" fill="#333">CSL Ltd</text><text x="115" y="51" fontSize="6" fill="#333">AU</text><text x="170" y="51" fontSize="6" fill="#333">2.8%</text>
      <text x="20" y="65" fontSize="6" fill="#333">Microsoft</text><text x="115" y="65" fontSize="6" fill="#333">US</text><text x="170" y="65" fontSize="6" fill="#333">2.5%</text>
    </svg>
  ),
  contribution_table: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <text x="100" y="14" textAnchor="middle" fontSize="8" fill="#6c757d">Contribution to Return</text>
      <rect x="10" y="20" width="85" height="14" rx="2" fill="#072040" /><text x="52" y="30" textAnchor="middle" fontSize="7" fill="white">Top 5</text>
      <rect x="105" y="20" width="85" height="14" rx="2" fill="#072040" /><text x="147" y="30" textAnchor="middle" fontSize="7" fill="white">Bottom 5</text>
      {[0,1,2].map(i => <React.Fragment key={i}>
        <rect x="10" y={38+i*14} width="85" height="12" fill="#fff" stroke="#eee" strokeWidth="0.5" />
        <text x="15" y={47+i*14} fontSize="6" fill="#91CF9B">+0.{3-i}%</text>
        <rect x="105" y={38+i*14} width="85" height="12" fill="#fff" stroke="#eee" strokeWidth="0.5" />
        <text x="110" y={47+i*14} fontSize="6" fill="#f0a500">-0.{i+1}%</text>
      </React.Fragment>)}
    </svg>
  ),
  market_review_table: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <rect x="10" y="10" width="180" height="14" rx="2" fill="#072040" />
      <text x="20" y="20" fontSize="7" fill="white">Index</text><text x="105" y="20" fontSize="7" fill="white">3M</text><text x="135" y="20" fontSize="7" fill="white">6M</text><text x="165" y="20" fontSize="7" fill="white">1Y</text>
      <rect x="10" y="27" width="180" height="11" fill="#E7E6E6" /><text x="15" y="35" fontSize="6" fill="#333" fontWeight="bold">Equities</text>
      {[0,1].map(i => <rect key={i} x="10" y={40+i*12} width="180" height="11" fill="#fff" stroke="#eee" strokeWidth="0.5" />)}
      <text x="20" y="48" fontSize="6" fill="#333">ASX 200</text><text x="108" y="48" fontSize="6" fill="#91CF9B">+2.1%</text>
      <text x="20" y="60" fontSize="6" fill="#333">S&amp;P 500</text><text x="108" y="60" fontSize="6" fill="#91CF9B">+3.4%</text>
      <rect x="10" y="65" width="180" height="11" fill="#E7E6E6" /><text x="15" y="73" fontSize="6" fill="#333" fontWeight="bold">Fixed Income</text>
    </svg>
  ),
  market_review_monthly_table: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <rect x="10" y="10" width="180" height="14" rx="2" fill="#072040" />
      <text x="20" y="20" fontSize="7" fill="white">Index</text><text x="85" y="20" fontSize="7" fill="white">1M</text><text x="110" y="20" fontSize="7" fill="white">3M</text><text x="140" y="20" fontSize="7" fill="white">6M</text><text x="170" y="20" fontSize="7" fill="white">1Y</text>
      <rect x="10" y="27" width="180" height="11" fill="#E7E6E6" /><text x="15" y="35" fontSize="6" fill="#333" fontWeight="bold">Equities</text>
      {[0,1].map(i => <rect key={i} x="10" y={40+i*12} width="180" height="11" fill="#fff" stroke="#eee" strokeWidth="0.5" />)}
      <text x="20" y="48" fontSize="6" fill="#333">ASX 200</text><text x="85" y="48" fontSize="6" fill="#91CF9B">+1.2%</text><text x="110" y="48" fontSize="6" fill="#91CF9B">+2.1%</text>
      <text x="20" y="60" fontSize="6" fill="#333">S&amp;P 500</text><text x="85" y="60" fontSize="6" fill="#91CF9B">+0.8%</text><text x="110" y="60" fontSize="6" fill="#91CF9B">+3.4%</text>
      <rect x="10" y="65" width="180" height="11" fill="#E7E6E6" /><text x="15" y="73" fontSize="6" fill="#333" fontWeight="bold">Fixed Income</text>
    </svg>
  ),
  tactical_tilts_table: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <text x="10" y="14" fontSize="8" fill="#072040" fontWeight="bold">TACTICAL TILTS*</text>
      {[
        ['Aus Equities', '#8E6B8E', 'Underweight'],
        ['Intl Equities', '#337B91', 'Overweight'],
        ['Real Estate', '#C0C0C0', 'Neutral'],
        ['Infrastructure', '#337B91', 'Overweight'],
        ['Govt Bonds', '#8E6B8E', 'Underweight'],
        ['Corporate Debt', '#337B91', 'Overweight'],
        ['Cash', '#C0C0C0', 'Neutral'],
      ].map(([label, colour, pos], i) => (
        <React.Fragment key={i}>
          <text x="10" y={30 + i * 13} fontSize="6" fill="#333">{label}</text>
          <rect x="75" y={23 + i * 13} width="115" height="11" rx="1" fill={colour} />
          <text x="132" y={31 + i * 13} fontSize="6" fill="white" textAnchor="middle">{pos}</text>
        </React.Fragment>
      ))}
    </svg>
  ),
  asset_class_summary_table: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <rect x="10" y="10" width="180" height="16" rx="2" fill="#072040" />
      <text x="20" y="21" fontSize="7" fill="white">Asset Class</text><text x="100" y="21" fontSize="7" fill="white">Wt%</text><text x="130" y="21" fontSize="7" fill="white">SAA%</text><text x="162" y="21" fontSize="7" fill="white">Tilt</text>
      {[0,1,2,3,4].map(i => <rect key={i} x="10" y={30 + i*16} width="180" height="14" fill={i%2 ? '#f8f9fa' : '#fff'} stroke="#eee" strokeWidth="0.5" />)}
      <text x="20" y="41" fontSize="6" fill="#333">Aus Eq</text><text x="103" y="41" fontSize="6" fill="#333">25.0</text><text x="133" y="41" fontSize="6" fill="#333">22.0</text><text x="162" y="41" fontSize="6" fill="#599BE3">+3.0</text>
      <text x="20" y="57" fontSize="6" fill="#333">Int Eq</text><text x="103" y="57" fontSize="6" fill="#333">30.0</text><text x="133" y="57" fontSize="6" fill="#333">32.0</text><text x="162" y="57" fontSize="6" fill="#f0a500">-2.0</text>
      <text x="20" y="73" fontSize="6" fill="#333">Bonds</text><text x="103" y="73" fontSize="6" fill="#333">20.0</text><text x="133" y="73" fontSize="6" fill="#333">20.0</text><text x="162" y="73" fontSize="6" fill="#6c757d">0.0</text>
    </svg>
  ),
  risk_metrics_table: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <rect x="10" y="10" width="180" height="16" rx="2" fill="#072040" />
      <text x="20" y="21" fontSize="7" fill="white">Metric</text><text x="105" y="21" fontSize="7" fill="white">Portfolio</text><text x="158" y="21" fontSize="7" fill="white">Peer</text>
      {[0,1,2,3,4].map(i => <rect key={i} x="10" y={30 + i*16} width="180" height="14" fill={i%2 ? '#f8f9fa' : '#fff'} stroke="#eee" strokeWidth="0.5" />)}
      <text x="20" y="41" fontSize="6" fill="#333">Volatility</text><text x="115" y="41" fontSize="6" fill="#333">8.2%</text><text x="163" y="41" fontSize="6" fill="#333">9.1%</text>
      <text x="20" y="57" fontSize="6" fill="#333">Sharpe</text><text x="115" y="57" fontSize="6" fill="#333">0.85</text><text x="163" y="57" fontSize="6" fill="#6c757d">{'\u2014'}</text>
      <text x="20" y="73" fontSize="6" fill="#333">Max DD</text><text x="115" y="73" fontSize="6" fill="#333">-12.3%</text><text x="163" y="73" fontSize="6" fill="#6c757d">{'\u2014'}</text>
      <text x="20" y="89" fontSize="6" fill="#333">Return 1Y</text><text x="115" y="89" fontSize="6" fill="#333">7.4%</text><text x="163" y="89" fontSize="6" fill="#333">6.8%</text>
    </svg>
  ),
  fund_comparison_table: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <rect x="10" y="10" width="180" height="16" rx="2" fill="#072040" />
      <text x="18" y="21" fontSize="7" fill="white">Fund</text><text x="80" y="21" fontSize="7" fill="white">Class</text><text x="125" y="21" fontSize="7" fill="white">Wt%</text><text x="155" y="21" fontSize="7" fill="white">G/D</text>
      {[0,1,2,3,4].map(i => <rect key={i} x="10" y={30 + i*16} width="180" height="14" fill={i%2 ? '#f8f9fa' : '#fff'} stroke="#eee" strokeWidth="0.5" />)}
      <text x="18" y="41" fontSize="6" fill="#333">Vanguard AE</text><text x="82" y="41" fontSize="6" fill="#333">Aus Eq</text><text x="130" y="41" fontSize="6" fill="#333">12.5</text><text x="158" y="41" fontSize="6" fill="#072040">Growth</text>
      <text x="18" y="57" fontSize="6" fill="#333">PIMCO GBF</text><text x="82" y="57" fontSize="6" fill="#333">Bonds</text><text x="130" y="57" fontSize="6" fill="#333">10.0</text><text x="158" y="57" fontSize="6" fill="#599BE3">Def</text>
      <text x="18" y="73" fontSize="6" fill="#333">MFS Intl</text><text x="82" y="73" fontSize="6" fill="#333">Int Eq</text><text x="130" y="73" fontSize="6" fill="#333">8.5</text><text x="158" y="73" fontSize="6" fill="#072040">Growth</text>
    </svg>
  ),
  quarterly_attribution_table: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <rect x="10" y="10" width="180" height="16" rx="2" fill="#072040" />
      <text x="25" y="21" fontSize="7" fill="white">#</text><text x="50" y="21" fontSize="7" fill="white">Asset Class</text><text x="148" y="21" fontSize="7" fill="white">Contrib %</text>
      {[0,1,2,3,4].map(i => <rect key={i} x="10" y={30 + i*16} width="180" height="14" fill={i%2 ? '#f8f9fa' : '#fff'} stroke="#eee" strokeWidth="0.5" />)}
      <text x="25" y="41" fontSize="6" fill="#333">1</text><text x="50" y="41" fontSize="6" fill="#333">Aus Equities</text><text x="155" y="41" fontSize="6" fill="#91CF9B">+0.45%</text>
      <text x="25" y="57" fontSize="6" fill="#333">2</text><text x="50" y="57" fontSize="6" fill="#333">Int Equities</text><text x="155" y="57" fontSize="6" fill="#91CF9B">+0.32%</text>
      <text x="25" y="73" fontSize="6" fill="#333">3</text><text x="50" y="73" fontSize="6" fill="#333">Cash</text><text x="155" y="73" fontSize="6" fill="#91CF9B">+0.05%</text>
      <text x="25" y="89" fontSize="6" fill="#333">4</text><text x="50" y="89" fontSize="6" fill="#333">Credit</text><text x="155" y="89" fontSize="6" fill="#f0a500">-0.12%</text>
    </svg>
  ),
  portfolio_characteristics_table: (
    <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="120" rx="4" fill="#f8f9fa" />
      <text x="100" y="14" textAnchor="middle" fontSize="8" fill="#6c757d">Portfolio Characteristics</text>
      {[0,1,2,3,4,5].map(i => <rect key={i} x="15" y={20 + i*16} width="170" height="14" fill={i%2 ? '#f8f9fa' : '#fff'} stroke="#eee" strokeWidth="0.5" />)}
      <text x="22" y="31" fontSize="6.5" fill="#333" fontWeight="600">Holdings Count</text><text x="160" y="31" textAnchor="end" fontSize="6.5" fill="#333">18</text>
      <text x="22" y="47" fontSize="6.5" fill="#333" fontWeight="600">Growth Exposure</text><text x="160" y="47" textAnchor="end" fontSize="6.5" fill="#333">65%</text>
      <text x="22" y="63" fontSize="6.5" fill="#333" fontWeight="600">Volatility (p.a.)</text><text x="160" y="63" textAnchor="end" fontSize="6.5" fill="#333">8.2%</text>
      <text x="22" y="79" fontSize="6.5" fill="#333" fontWeight="600">Sharpe Ratio</text><text x="160" y="79" textAnchor="end" fontSize="6.5" fill="#333">0.85</text>
      <text x="22" y="95" fontSize="6.5" fill="#333" fontWeight="600">Max Drawdown</text><text x="160" y="95" textAnchor="end" fontSize="6.5" fill="#333">-12.3%</text>
      <text x="22" y="111" fontSize="6.5" fill="#333" fontWeight="600">ICR</text><text x="160" y="111" textAnchor="end" fontSize="6.5" fill="#333">0.65%</text>
    </svg>
  ),
};

const AddBlockModal = ({ isOpen, onClose, onAdd }) => {
  const [activeTab, setActiveTab] = useState('text');
  const [heading, setHeading] = useState('');
  const [headingLevel, setHeadingLevel] = useState('h2');
  const [includeHeading, setIncludeHeading] = useState(false);
  const [includeBg, setIncludeBg] = useState(false);
  const [bgColor, setBgColor] = useState('#f5f5f5');
  const [hoveredTemplate, setHoveredTemplate] = useState(null);

  // Image state
  const [imagePreview, setImagePreview] = useState(null);
  const [imageLabel, setImageLabel] = useState('');
  const [objectFit, setObjectFit] = useState('contain');
  const fileInputRef = useRef(null);

  // Shape state
  const [shapeFill, setShapeFill] = useState('#072040');
  const [shapeStroke, setShapeStroke] = useState('#072040');
  const [shapeFilled, setShapeFilled] = useState(true);

  if (!isOpen) return null;

  const charts = TEMPLATE_LIBRARY.filter(t => t.category === 'chart');
  const tables = TEMPLATE_LIBRARY.filter(t => t.category === 'table');
  const isDataTab = activeTab === 'charts' || activeTab === 'tables';

  const handleAddText = () => {
    const blockId = generateBlockId('custom_text');
    const hasHeading = includeHeading && heading.trim();
    const label = hasHeading ? heading.trim() : 'Custom Text';
    const blockDef = {
      type: 'text',
      label,
      heading: hasHeading ? heading.trim() : null,
      headingLevel: hasHeading ? headingLevel : null,
      bgColor: includeBg ? bgColor : null,
    };
    onAdd(blockId, blockDef);
    setHeading('');
    setHeadingLevel('h2');
    setIncludeHeading(false);
    setIncludeBg(false);
    setBgColor('#f5f5f5');
    onClose();
  };

  const handleAddTemplate = (template) => {
    const blockId = generateBlockId(`tpl_${template.id}`);
    const blockDef = {
      type: 'data',
      label: template.label,
      templateId: template.id,
    };
    onAdd(blockId, blockDef);
    onClose();
  };

  const handleImageFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleAddImage = () => {
    if (!imagePreview) return;
    const blockId = generateBlockId('custom_image');
    const blockDef = {
      type: 'image',
      label: imageLabel.trim() || 'Custom Image',
      objectFit,
    };
    onAdd(blockId, blockDef, imagePreview);
    setImagePreview(null);
    setImageLabel('');
    setObjectFit('contain');
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  const handleAddShape = (shapeType) => {
    const blockId = generateBlockId('shape');
    const blockDef = {
      type: 'shape',
      label: SHAPE_OPTIONS.find(s => s.id === shapeType)?.label || 'Shape',
      shapeType,
      fillColor: shapeFilled ? shapeFill : 'transparent',
      strokeColor: shapeStroke,
      strokeWidth: 2,
      filled: shapeFilled,
    };
    onAdd(blockId, blockDef);
    onClose();
  };

  const renderTemplateList = (items) => (
    <div className="template-card-grid">
      {items.map((tpl) => (
        <div
          key={tpl.id}
          className={`template-card${hoveredTemplate === tpl.id ? ' selected' : ''}`}
          onMouseEnter={() => setHoveredTemplate(tpl.id)}
        >
          <div className="template-card-body">
            <div className="template-card-text">
              <strong>{tpl.label}</strong>
              <span className="template-desc">{tpl.description}</span>
            </div>
          </div>
          <button
            className="modal-add-btn small"
            onClick={() => handleAddTemplate(tpl)}
          >
            Add
          </button>
        </div>
      ))}
    </div>
  );

  // Determine which preview to show
  const activeItems = activeTab === 'charts' ? charts : tables;
  const previewId = hoveredTemplate && activeItems.some(t => t.id === hoveredTemplate)
    ? hoveredTemplate
    : activeItems[0]?.id;
  const previewTpl = activeItems.find(t => t.id === previewId);

  return createPortal(
    <div className="add-block-modal-overlay" onClick={onClose}>
      <div className={`add-block-modal${isDataTab ? ' wide' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add Block</h3>
          <button className="modal-close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-tabs">
          <button
            className={`modal-tab${activeTab === 'text' ? ' active' : ''}`}
            onClick={() => { setActiveTab('text'); setHoveredTemplate(null); }}
          >
            Text
          </button>
          <button
            className={`modal-tab${activeTab === 'image' ? ' active' : ''}`}
            onClick={() => { setActiveTab('image'); setHoveredTemplate(null); }}
          >
            Image
          </button>
          <button
            className={`modal-tab${activeTab === 'disclaimer' ? ' active' : ''}`}
            onClick={() => { setActiveTab('disclaimer'); setHoveredTemplate(null); }}
          >
            Disclaimer
          </button>
          <button
            className={`modal-tab${activeTab === 'shapes' ? ' active' : ''}`}
            onClick={() => { setActiveTab('shapes'); setHoveredTemplate(null); }}
          >
            Shapes
          </button>
          <button
            className={`modal-tab${activeTab === 'charts' ? ' active' : ''}`}
            onClick={() => { setActiveTab('charts'); setHoveredTemplate(null); }}
          >
            Charts
          </button>
          <button
            className={`modal-tab${activeTab === 'tables' ? ' active' : ''}`}
            onClick={() => { setActiveTab('tables'); setHoveredTemplate(null); }}
          >
            Tables
          </button>
        </div>

        <div className={`modal-body${isDataTab ? ' split-layout' : ''}`}>
          {activeTab === 'text' && (
            <div className="text-block-form">
              <label className="modal-checkbox-label">
                <input
                  type="checkbox"
                  checked={includeHeading}
                  onChange={(e) => setIncludeHeading(e.target.checked)}
                />
                Include heading
              </label>
              {includeHeading && (
                <>
                  <label className="modal-field-label">Heading Text</label>
                  <input
                    type="text"
                    className="modal-input"
                    value={heading}
                    onChange={(e) => setHeading(e.target.value)}
                    placeholder="e.g. Additional Commentary"
                  />
                  <label className="modal-field-label">Heading Level</label>
                  <select
                    className="modal-select"
                    value={headingLevel}
                    onChange={(e) => setHeadingLevel(e.target.value)}
                  >
                    <option value="h1">H1 — Section Heading</option>
                    <option value="h2">H2 — Sub-heading</option>
                  </select>
                </>
              )}
              <label className="modal-checkbox-label">
                <input
                  type="checkbox"
                  checked={includeBg}
                  onChange={(e) => setIncludeBg(e.target.checked)}
                />
                Background colour
              </label>
              {includeBg && (
                <div className="shape-colour-controls" style={{ marginTop: 4 }}>
                  <input
                    type="color"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    className="shape-colour-input"
                  />
                  <input
                    type="text"
                    className="modal-input"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    style={{ width: 90 }}
                  />
                </div>
              )}
              <button className="modal-add-btn" onClick={handleAddText}>
                Add Text Block
              </button>
            </div>
          )}

          {activeTab === 'image' && (
            <div className="text-block-form">
              <label className="modal-field-label">Upload Image</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="modal-file-input"
                onChange={handleImageFile}
              />
              {imagePreview && (
                <div className="image-preview-wrap">
                  <img src={imagePreview} alt="Preview" className="image-preview-thumb" />
                </div>
              )}
              <label className="modal-field-label">Label (optional)</label>
              <input
                type="text"
                className="modal-input"
                value={imageLabel}
                onChange={(e) => setImageLabel(e.target.value)}
                placeholder="e.g. Portfolio Diagram"
              />
              <label className="modal-field-label">Fit Mode</label>
              <select
                className="modal-select"
                value={objectFit}
                onChange={(e) => setObjectFit(e.target.value)}
              >
                <option value="contain">Contain (fit within block)</option>
                <option value="cover">Cover (fill block, may crop)</option>
                <option value="fill">Stretch to fill</option>
              </select>
              <button
                className="modal-add-btn"
                onClick={handleAddImage}
                disabled={!imagePreview}
              >
                Add Image Block
              </button>
            </div>
          )}

          {activeTab === 'disclaimer' && (
            <DisclaimerBlockPicker
              defaultKey="quarterly_report"
              generateBlockId={generateBlockId}
              onAdd={onAdd}
              onClose={onClose}
            />
          )}

          {activeTab === 'shapes' && (
            <div className="text-block-form">
              <div className="shape-colour-controls">
                <label className="modal-field-label">
                  Fill
                  <input
                    type="color"
                    value={shapeFill}
                    onChange={(e) => setShapeFill(e.target.value)}
                    className="shape-colour-input"
                  />
                </label>
                <label className="modal-field-label">
                  Stroke
                  <input
                    type="color"
                    value={shapeStroke}
                    onChange={(e) => setShapeStroke(e.target.value)}
                    className="shape-colour-input"
                  />
                </label>
                <label className="modal-checkbox-label">
                  <input
                    type="checkbox"
                    checked={shapeFilled}
                    onChange={(e) => setShapeFilled(e.target.checked)}
                  />
                  Filled
                </label>
              </div>
              <div className="shape-grid">
                {SHAPE_OPTIONS.map((shape) => (
                  <button
                    key={shape.id}
                    className="shape-option-btn"
                    onClick={() => handleAddShape(shape.id)}
                    title={shape.label}
                  >
                    <span className="shape-option-icon">{shape.icon}</span>
                    <span className="shape-option-label">{shape.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {isDataTab && (
            <>
              <div className="template-list-panel">
                {activeTab === 'charts' && renderTemplateList(charts)}
                {activeTab === 'tables' && renderTemplateList(tables)}
              </div>
              <div className="template-preview-panel">
                {previewTpl && (
                  <>
                    <div className="preview-thumbnail">
                      {PREVIEW_THUMBNAILS[previewId] || (
                        <div className="preview-placeholder">No preview available</div>
                      )}
                    </div>
                    <div className="preview-info">
                      <strong>{previewTpl.label}</strong>
                      <span>{previewTpl.description}</span>
                      <span className="preview-data-key">Data: <code>{previewTpl.dataKey}</code></span>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddBlockModal;
